import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import {
    SpellUsageAnalysisData,
    SpellUsageBatchAnalysisData,
    SpellUsageFightMetrics,
    SpellUsageSpellMetrics,
} from '../types';
import TbcErrorBanner from '../tbcAnalyzer/components/TbcErrorBanner';
import TbcLogUrlPhase from '../tbcAnalyzer/components/TbcLogUrlPhase';
import TbcPhaseHeader from '../tbcAnalyzer/components/TbcPhaseHeader';
import TbcSelectionPhase from '../tbcAnalyzer/components/TbcSelectionPhase';
import TbcSimpleBreakdownTable from '../tbcAnalyzer/components/TbcSimpleBreakdownTable';
import { useTbcAnalyzerSession } from '../tbcAnalyzer/useTbcAnalyzerSession';
import { TbcReportRouteProps, useTbcReportRoute } from '../tbcAnalyzer/useTbcReportRoute';
import { fightFilterLabel, formatDuration, formatNumber, groupRowsByName } from '../tbcAnalyzer/utils';
import HelpTip from './HelpTip';

type SpellSortKey =
    | 'spellName'
    | 'casts'
    | 'castCpm'
    | 'hits'
    | 'critPercent'
    | 'effectiveHealing'
    | 'overhealPercent'
    | 'avgEffective'
    | 'hasteHpsPerPoint'
    | 'plusHealHpsPerPoint'
    | 'critHpsPerPoint';
type SortDirection = 'asc' | 'desc';

const LEGACY_LOG_URLS_KEY = 'wow_calcs.statValueAnalyzer.logUrls';

const SPELL_SORT_COLUMNS: Array<{ key: SpellSortKey; label: string }> = [
    { key: 'spellName', label: 'Spell' },
    { key: 'casts', label: 'Casts' },
    { key: 'castCpm', label: 'CPM' },
    { key: 'hits', label: 'Hits' },
    { key: 'critPercent', label: 'Crit %' },
    { key: 'effectiveHealing', label: 'Effective' },
    { key: 'overhealPercent', label: 'Overheal %' },
    { key: 'avgEffective', label: 'Avg (eff)' },
    { key: 'hasteHpsPerPoint', label: 'Haste HPS/pt' },
    { key: 'plusHealHpsPerPoint', label: '+Heal HPS/pt' },
    { key: 'critHpsPerPoint', label: 'Crit HPS/pt' },
];

const PER_FIGHT_COLUMNS: Array<{
    key: string;
    label: string;
    render: (fight: SpellUsageFightMetrics) => React.ReactNode;
}> = [
    { key: 'casts', label: 'Casts', render: (fight) => formatNumber(fight.casts) },
    { key: 'cpm', label: 'CPM', render: (fight) => formatNumber(fight.castCpm, 2) },
    { key: 'hits', label: 'Hits', render: (fight) => formatNumber(fight.hits) },
    { key: 'crit', label: 'Crit %', render: (fight) => `${formatNumber(fight.critPercent, 1)}%` },
    { key: 'effective', label: 'Effective', render: (fight) => formatNumber(fight.effectiveHealing) },
    { key: 'overheal', label: 'Overheal %', render: (fight) => `${formatNumber(fight.overhealPercent, 1)}%` },
    { key: 'haste', label: 'Haste HPS/pt', render: (fight) => formatNumber(fight.hasteHpsPerPoint, 3) },    { key: 'plusHeal', label: '+Heal HPS/pt', render: (fight) => formatNumber(fight.plusHealHpsPerPoint, 3) },
    { key: 'critPt', label: 'Crit HPS/pt', render: (fight) => formatNumber(fight.critHpsPerPoint, 3) },
];

const compareSpellSortValue = (a: SpellUsageSpellMetrics, b: SpellUsageSpellMetrics, key: SpellSortKey): number => {
    if (key === 'spellName') {
        return a.spellName.localeCompare(b.spellName);
    }
    return a[key] - b[key];
};

const SpellPerFightBreakdown: React.FC<{ fights: SpellUsageFightMetrics[] }> = ({ fights }) => {
    const groups = groupRowsByName(fights);

    return (
        <div className="table-container" style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table className="breakdown-table">
                <thead>
                    <tr>
                        <th>Fight</th>
                        {PER_FIGHT_COLUMNS.map((column) => (
                            <th key={column.key}>{column.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((group) => (
                        <React.Fragment key={group.name}>
                            <tr className="boss-group-header">
                                <td colSpan={PER_FIGHT_COLUMNS.length + 1}>
                                    {group.name}
                                    <span className="boss-group-count">
                                        {' '}
                                        — {group.rows.length} pull
                                        {group.rows.length === 1 ? '' : 's'}
                                    </span>
                                </td>
                            </tr>
                            {group.rows.map((fight) => (
                                <tr key={`${group.name}-${fight.fightId}`}>
                                    <td>{fight.fightName}</td>
                                    {PER_FIGHT_COLUMNS.map((column) => (
                                        <td key={column.key}>{column.render(fight)}</td>
                                    ))}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

interface HealerStatValueDetailProps {
    result: SpellUsageAnalysisData;
    filterLabel: string;
    breakdownRef?: React.Ref<HTMLDivElement>;
}

/** Full stat value report for one healer: global values, summary, per-spell / per-boss breakdown. */
const HealerStatValueDetail: React.FC<HealerStatValueDetailProps> = ({ result, filterLabel, breakdownRef }) => {
    const [expandedSpellId, setExpandedSpellId] = useState<number | null>(null);
    const [sortKey, setSortKey] = useState<SpellSortKey>('castCpm');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSpellSort = (key: SpellSortKey) => {
        if (sortKey === key) {
            setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
            return;
        }
        setSortKey(key);
        setSortDirection('desc');
    };

    const sortedSpells = useMemo(() => {
        if (!result) {
            return [];
        }
        const spells = [...result.spells];
        spells.sort((a, b) => {
            const comparison = compareSpellSortValue(a, b, sortKey);
            if (comparison !== 0) {
                return sortDirection === 'asc' ? comparison : -comparison;
            }
            return a.spellName.localeCompare(b.spellName);
        });
        return spells;
    }, [result, sortKey, sortDirection]);

    const sortIndicator = (key: SpellSortKey): string => {
        if (sortKey !== key) {
            return '';
        }
        return sortDirection === 'desc' ? ' ↓' : ' ↑';
    };

    const spellColumns = useMemo(
        () =>
            SPELL_SORT_COLUMNS.map((column) => ({
                key: column.key,
                header: (
                    <>
                        {column.label}
                        {sortIndicator(column.key)}
                    </>
                ),
                headerClassName: 'sortable-th',
                render: (spell: SpellUsageSpellMetrics) => {
                    if (column.key === 'spellName') {
                        return (
                            <>
                                <span title={`Spell ID ${spell.spellId}`}>{spell.spellName}</span>
                            </>
                        );
                    }
                    if (column.key === 'critPercent' || column.key === 'overhealPercent') {
                        return `${formatNumber(spell[column.key], 1)}%`;
                    }
                    if (
                        column.key === 'castCpm' ||
                        column.key === 'avgEffective' ||
                        column.key === 'hasteHpsPerPoint' ||
                        column.key === 'plusHealHpsPerPoint' ||
                        column.key === 'critHpsPerPoint'
                    ) {
                        const decimals =
                            column.key === 'castCpm' || column.key === 'avgEffective'
                                ? column.key === 'castCpm'
                                    ? 2
                                    : 1
                                : 3;
                        return formatNumber(spell[column.key], decimals);
                    }
                    return formatNumber(spell[column.key as 'casts' | 'hits' | 'effectiveHealing']);
                },
            })),
        [sortKey, sortDirection]
    );

    return (
        <>
            <div className="mp5-showcase">
                <div className="mp5-card">
                    <div className="mp5-header">
                        <h3>Global Stat Values</h3>
                        <div className="mp5-value">
                            <span className="mp5-number">
                                {formatNumber(result.globalStatValues.hasteHpsPerPoint, 3)}
                            </span>
                            <span className="mp5-label">
                                Haste HPS / point
                                {result.globalStatValues.hasteActivityFactor > 0
                                    ? ` (×${formatNumber(result.globalStatValues.hasteActivityFactor * 100, 1)}% busy)`
                                    : ''}                            </span>
                        </div>
                        <div className="mp5-value" style={{ marginTop: '12px' }}>
                            <span className="mp5-number">
                                {formatNumber(result.globalStatValues.plusHealHpsPerPoint, 3)}
                            </span>
                            <span className="mp5-label">+Heal HPS / point</span>
                        </div>
                        <div className="mp5-value" style={{ marginTop: '12px' }}>
                            <span className="mp5-number">
                                {formatNumber(result.globalStatValues.critHpsPerPoint, 3)}
                            </span>
                            <span className="mp5-label">Crit HPS / point</span>
                        </div>
                        <div className="mp5-value" style={{ marginTop: '12px' }}>
                            <span className="mp5-number">
                                {result.globalStatValues.plusHealPerHastePoint === null
                                    ? '—'
                                    : formatNumber(result.globalStatValues.plusHealPerHastePoint, 2)}
                            </span>
                            <span className="mp5-label">+Heal ≈ 1 Haste</span>
                        </div>
                        <div className="mp5-value" style={{ marginTop: '12px' }}>
                            <span className="mp5-number">
                                {result.globalStatValues.plusHealPerCritPoint === null
                                    ? '—'
                                    : formatNumber(result.globalStatValues.plusHealPerCritPoint, 2)}
                            </span>
                            <span className="mp5-label">+Heal ≈ 1 Crit</span>
                        </div>
                    </div>
                    <div className="mp5-subtitle">
                        {result.selectedPlayer
                            ? `${result.selectedPlayer.name} (${result.selectedPlayer.class})`
                            : 'Unknown healer'}
                        {filterLabel ? ` · ${filterLabel}` : ''} · {result.reportIds.length} log
                        {result.reportIds.length === 1 ? '' : 's'} · {result.fightsProcessed} fight
                        {result.fightsProcessed === 1 ? '' : 's'}
                        {result.duplicatesRemoved > 0
                            ? ` · −${result.duplicatesRemoved} duplicate pull${
                                  result.duplicatesRemoved === 1 ? '' : 's'
                              }`
                            : ''}
                    </div>
                </div>
            </div>

            <div className="results-summary">
                <div className="summary-card">
                    <h4>Summary</h4>
                    <div className="summary-stats">
                        <div className="stat">
                            <span className="stat-label">Fight Duration:</span>
                            <span className="stat-value">{formatDuration(result.totalFightDurationSeconds)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">Total Cast CPM:</span>
                            <span className="stat-value">{formatNumber(result.globalStatValues.totalCastCpm, 2)}</span>
                        </div>
                        {result.globalStatValues.castActivity && (
                            <>
                                <div className="stat">
                                    <span className="stat-label">Activity %:</span>
                                    <span className="stat-value">
                                        {formatNumber(result.globalStatValues.castActivity.activityPercent, 1)}%
                                    </span>
                                </div>
                                {result.globalStatValues.haste && (
                                    <div className="stat">
                                        <span className="stat-label heading-with-help">
                                            Existing haste:
                                            <HelpTip label="Existing haste">
                                                <p>
                                                    Haste you already had: gear rating from the log, plus
                                                    Bloodlust/Heroism, Power Infusion, Drums of Battle and Berserking
                                                    while they were up. Item procs are not counted.
                                                </p>
                                                <p>
                                                    Busy time uses your hasted cast times and GCD, and haste value is
                                                    for your <em>next</em> point of haste.
                                                </p>
                                            </HelpTip>
                                        </span>
                                        <span className="stat-value">
                                            {formatNumber(result.globalStatValues.haste.gearHasteRating, 0)} rating ·
                                            avg +{formatNumber(result.globalStatValues.haste.meanHastePercent, 1)}%
                                            speed
                                            {result.globalStatValues.haste.buffs.length > 0
                                                ? ` (${result.globalStatValues.haste.buffs
                                                      .map(
                                                          (buff) =>
                                                              `${buff.name} ${formatNumber(buff.uptimePercent, 1)}%`
                                                      )
                                                      .join(', ')} of the time)`
                                                : ''}
                                        </span>
                                    </div>
                                )}
                                <div className="stat">
                                    <span className="stat-label">Haste (before → after busy time):</span>
                                    <span className="stat-value">
                                        {formatNumber(result.globalStatValues.hasteHpsPerPointRaw, 3)} →{' '}
                                        {formatNumber(result.globalStatValues.hasteHpsPerPoint, 3)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="spell-breakdown">
                <h4 className="heading-with-help">
                    Spell Breakdown
                    <HelpTip label="Spell breakdown">
                        <p>Click a row to expand per-boss numbers for that spell.</p>
                    </HelpTip>
                </h4>
                <p className="analyzer-description">Click a row for per-boss numbers.</p>
                <div ref={breakdownRef} onClick={(event) => event.stopPropagation()}>
                    <TbcSimpleBreakdownTable
                        rows={sortedSpells}
                        columns={spellColumns.map((column) => ({
                            ...column,
                            header: (
                                <span
                                    onClick={() => handleSpellSort(column.key as SpellSortKey)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            handleSpellSort(column.key as SpellSortKey);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {column.header}
                                </span>
                            ),
                        }))}
                        rowKey={(spell) => String(spell.spellId)}
                        emptyMessage="No spell usage found for this player with the selected filter."
                        onRowClick={(spell) => {
                            setExpandedSpellId((current) => (current === spell.spellId ? null : spell.spellId));
                        }}
                        expandedRowKey={expandedSpellId !== null ? String(expandedSpellId) : null}
                        renderExpandedRow={(spell) => <SpellPerFightBreakdown fights={spell.perFight} />}
                    />
                </div>
            </div>

            {result.globalStatValues.castActivity && (
                <div className="results-summary" style={{ marginTop: '24px' }}>
                    <div className="summary-card">
                        <details>
                            <summary className="heading-with-help" style={{ cursor: 'pointer', fontWeight: 600 }}>
                                Time spent casting ({result.globalStatValues.castActivity.busyBreakdown.length} spells)
                                <HelpTip label="Time spent casting">
                                    <p>
                                        How each spell used your time (cast time and GCD). This is what scales haste
                                        value — more time spent casting means haste is worth more.
                                    </p>
                                </HelpTip>
                            </summary>
                            <div className="table-container" style={{ overflowX: 'auto', marginTop: '12px' }}>
                                <table className="breakdown-table">
                                    <thead>
                                        <tr>
                                            <th>Spell</th>
                                            <th>Source</th>
                                            <th>Casts</th>
                                            <th>Sec / cast</th>
                                            <th>Casting sec</th>
                                            <th>% of time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.globalStatValues.castActivity.busyBreakdown.map((row) => {
                                            const busyShare =
                                                result.globalStatValues.castActivity.busySeconds > 0
                                                    ? (row.busySeconds /
                                                          result.globalStatValues.castActivity.busySeconds) *
                                                      100
                                                    : 0;
                                            return (
                                                <tr key={row.spellId}>
                                                    <td title={`Spell ID ${row.spellId}`}>{row.spellName}</td>
                                                    <td>{row.source === 'utility' ? 'Utility' : 'Heal'}</td>
                                                    <td>{formatNumber(row.casts)}</td>
                                                    <td>{formatNumber(row.secondsPerCast, 2)}</td>
                                                    <td>{formatNumber(row.busySeconds, 1)}</td>
                                                    <td>{formatNumber(busyShare, 1)}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </details>

                        <details style={{ marginTop: '12px' }}>
                            <summary className="heading-with-help" style={{ cursor: 'pointer', fontWeight: 600 }}>
                                Other casts ({result.globalStatValues.castActivity.untrackedCasts})
                                <HelpTip label="Other casts">
                                    <p>
                                        Casts that are not modeled for haste (unknown spells or things we skip). They
                                        are listed here so you can see what was left out.
                                    </p>
                                </HelpTip>
                            </summary>
                            {result.globalStatValues.castActivity.untrackedBreakdown.length === 0 ? (
                                <p className="results-status" style={{ marginTop: '12px' }}>
                                    None.
                                </p>
                            ) : (
                                <div className="table-container" style={{ overflowX: 'auto', marginTop: '12px' }}>
                                    <table className="breakdown-table">
                                        <thead>
                                            <tr>
                                                <th>Spell</th>
                                                <th>Casts</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.globalStatValues.castActivity.untrackedBreakdown.map((row) => (
                                                <tr key={row.spellId}>
                                                    <td title={`Spell ID ${row.spellId}`}>{row.spellName}</td>
                                                    <td>{formatNumber(row.casts)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </details>
                    </div>
                </div>
            )}
        </>
    );
};

type ComparisonSortKey =
    | 'name'
    | 'fightsProcessed'
    | 'activityPercent'
    | 'meanHastePercent'
    | 'hasteHpsPerPoint'
    | 'plusHealHpsPerPoint'
    | 'critHpsPerPoint'
    | 'plusHealPerHastePoint'
    | 'plusHealPerCritPoint';

const comparisonSortValue = (healer: SpellUsageAnalysisData, key: ComparisonSortKey): number | string => {
    const stats = healer.globalStatValues;
    switch (key) {
        case 'name':
            return healer.selectedPlayer?.name ?? '';
        case 'fightsProcessed':
            return healer.fightsProcessed;
        case 'activityPercent':
            return stats.castActivity?.activityPercent ?? 0;
        case 'meanHastePercent':
            return stats.haste?.meanHastePercent ?? 0;
        case 'plusHealPerHastePoint':
            return stats.plusHealPerHastePoint ?? -1;        case 'plusHealPerCritPoint':
            return stats.plusHealPerCritPoint ?? -1;
        default:
            return stats[key];
    }
};

const healerKey = (healer: SpellUsageAnalysisData): string => healer.selectedPlayer?.name ?? 'Unknown healer';

const COMPARISON_COLUMNS: Array<{
    key: ComparisonSortKey;
    label: string;
    render: (healer: SpellUsageAnalysisData) => React.ReactNode;
}> = [
    {
        key: 'name',
        label: 'Healer',
        render: (healer) =>
            healer.selectedPlayer ? `${healer.selectedPlayer.name} (${healer.selectedPlayer.class})` : 'Unknown healer',
    },
    { key: 'fightsProcessed', label: 'Fights', render: (healer) => formatNumber(healer.fightsProcessed) },
    {
        key: 'activityPercent',
        label: 'Activity %',
        render: (healer) => `${formatNumber(healer.globalStatValues.castActivity?.activityPercent ?? 0, 1)}%`,
    },
    {
        key: 'meanHastePercent',
        label: 'Avg haste',
        render: (healer) =>
            healer.globalStatValues.haste
                ? `${formatNumber(healer.globalStatValues.haste.gearHasteRating, 0)} rtg · +${formatNumber(
                      healer.globalStatValues.haste.meanHastePercent,
                      1
                  )}%`
                : '—',
    },
    {
        key: 'hasteHpsPerPoint',
        label: 'Haste HPS/pt',
        render: (healer) => formatNumber(healer.globalStatValues.hasteHpsPerPoint, 3),
    },    {
        key: 'plusHealHpsPerPoint',
        label: '+Heal HPS/pt',
        render: (healer) => formatNumber(healer.globalStatValues.plusHealHpsPerPoint, 3),
    },
    {
        key: 'critHpsPerPoint',
        label: 'Crit HPS/pt',
        render: (healer) => formatNumber(healer.globalStatValues.critHpsPerPoint, 3),
    },
    {
        key: 'plusHealPerHastePoint',
        label: '+Heal ≈ 1 Haste',
        render: (healer) =>
            healer.globalStatValues.plusHealPerHastePoint === null
                ? '—'
                : formatNumber(healer.globalStatValues.plusHealPerHastePoint, 2),
    },    {
        key: 'plusHealPerCritPoint',
        label: '+Heal ≈ 1 Crit',
        render: (healer) =>
            healer.globalStatValues.plusHealPerCritPoint === null
                ? '—'
                : formatNumber(healer.globalStatValues.plusHealPerCritPoint, 2),
    },
];

/** Reports saved before multi-healer support hold a single healer's result. */
const normalizeBatchResult = (
    restored: SpellUsageBatchAnalysisData | SpellUsageAnalysisData
): SpellUsageBatchAnalysisData => ('healers' in restored ? restored : { healers: [restored], missingPlayerNames: [] });

const StatValueAnalyzer: React.FC<TbcReportRouteProps> = ({ basePath, reportId }) => {
    const session = useTbcAnalyzerSession({ legacyLogUrlsKey: LEGACY_LOG_URLS_KEY });
    const [selectedPlayerNames, setSelectedPlayerNames] = useState<string[]>([]);
    const [result, setResult] = useState<SpellUsageBatchAnalysisData | null>(null);
    const [focusedHealerKey, setFocusedHealerKey] = useState<string | null>(null);
    const [comparisonSortKey, setComparisonSortKey] = useState<ComparisonSortKey>('hasteHpsPerPoint');
    const [comparisonSortDirection, setComparisonSortDirection] = useState<SortDirection>('desc');
    const resultsRef = useRef<HTMLDivElement>(null);
    const reportRoute = useTbcReportRoute<SpellUsageBatchAnalysisData | SpellUsageAnalysisData, string[]>({
        basePath,
        reportId,
        session,
        onRestore: (restored, restoredPlayerNames) => {
            const normalized = normalizeBatchResult(restored);
            setResult(normalized);
            setFocusedHealerKey(null);
            setSelectedPlayerNames(
                restoredPlayerNames ?? normalized.healers.map((healer) => healer.selectedPlayer?.name ?? '')
            );
        },
        onLeave: () => {
            setResult(null);
            setFocusedHealerKey(null);
        },
    });

    // Stat values are most useful side by side: default to every healer in the logs.
    useEffect(() => {
        if (session.players.length === 0) {
            return;
        }
        const validNames = new Set(session.players.map((player) => player.name));
        setSelectedPlayerNames((current) => {
            const kept = current.filter((name) => validNames.has(name));
            if (kept.length > 0) {
                return kept;
            }
            return session.players.map((player) => player.name);
        });
    }, [session.players]);

    const handleTogglePlayerName = (name: string) => {
        setSelectedPlayerNames((current) => {
            if (current.indexOf(name) !== -1) {
                return current.filter((entry) => entry !== name);
            }
            return [...current, name];
        });
    };

    const handleProcessAnalysis = async () => {
        if (selectedPlayerNames.length === 0) {
            session.setError('Please select at least one healer for analysis');
            return;
        }
        if (session.loadedLogUrls.length === 0) {
            session.setError('No logs loaded');
            return;
        }
        if (session.bosses.length > 0 && session.selectedEncounterIds.length === 0) {
            session.setError('Please select at least one boss');
            return;
        }

        session.setLoading(true);
        session.setError(null);
        setResult(null);
        setFocusedHealerKey(null);
        const wclProgressId = session.beginWclProgress();

        try {
            const orderedNames = session.players
                .map((player) => player.name)
                .filter((name) => selectedPlayerNames.indexOf(name) !== -1);
            const response = await axios.post<SpellUsageBatchAnalysisData>('/api/attendance/analyze-spell-usage', {
                ...session.sharedRequestBody,
                selectedPlayerNames: orderedNames,
                wclProgressId,
            });
            setResult(response.data);
            session.setPhase('results');
            reportRoute.publish(response.data, selectedPlayerNames);
        } catch (err: any) {
            console.error('Error analyzing spell usage:', err);
            session.setError(
                err.response?.data?.error || err.response?.data?.details || 'Failed to analyze logs. Please try again.'
            );
        } finally {
            session.endWclProgress();
            session.setLoading(false);
        }
    };

    const handleBackToUrl = () => {
        session.handleBackToUrl();
        setResult(null);
        setFocusedHealerKey(null);
        setSelectedPlayerNames([]);
    };

    const handleComparisonSort = (key: ComparisonSortKey) => {
        if (comparisonSortKey === key) {
            setComparisonSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
            return;
        }
        setComparisonSortKey(key);
        setComparisonSortDirection(key === 'name' ? 'asc' : 'desc');
    };

    const healers = result?.healers ?? [];
    const singleHealer = healers.length === 1 ? healers[0] : null;
    const focusedHealer = healers.find((healer) => healerKey(healer) === focusedHealerKey) ?? null;

    const sortedHealers = useMemo(() => {
        const rows = [...healers];
        rows.sort((a, b) => {
            const aValue = comparisonSortValue(a, comparisonSortKey);
            const bValue = comparisonSortValue(b, comparisonSortKey);
            const comparison =
                typeof aValue === 'string' || typeof bValue === 'string'
                    ? String(aValue).localeCompare(String(bValue))
                    : aValue - bValue;
            if (comparison !== 0) {
                return comparisonSortDirection === 'asc' ? comparison : -comparison;
            }
            return healerKey(a).localeCompare(healerKey(b));
        });
        return rows;
    }, [healers, comparisonSortKey, comparisonSortDirection]);

    const filterLabel = result?.healers[0]
        ? fightFilterLabel(result.healers[0].fightFilter, session.bosses, result.healers[0].fightFilterLabel)
        : '';

    const analyzeButtonLabel =
        selectedPlayerNames.length <= 1
            ? 'Analyze Stat Values'
            : `Analyze Stat Values (${selectedPlayerNames.length} healers)`;

    const statValueHelp = (
        <HelpTip label="How stat values are calculated" wide>
            <h4>What this shows</h4>
            <p>How much HPS you gain from 1 haste, 1 +healing, and 1 crit, based on the heals you actually cast.</p>
            <ul>
                <li>
                    <strong>Haste</strong> is scaled by how busy you were (time spent casting vs standing around), and
                    is worth a bit less the more haste you already have.
                </li>
                <li>
                    <strong>+Heal</strong> and <strong>crit</strong> use the healing those spells did on these fights.
                </li>
                <li>
                    <strong>+Heal ≈ 1 Haste / Crit</strong> is how much +healing matches one point of that stat.
                </li>
            </ul>
            <p>Click a spell row to see the same numbers per boss.</p>
        </HelpTip>
    );

    return (
        <div className="item-set-calculator">
            {session.phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        Stat Values
                        {statValueHelp}
                    </h2>
                    <p className="analyzer-description">
                        Haste, +healing, and crit value from your healing on these logs.
                    </p>
                </>
            )}

            <TbcErrorBanner error={session.error} />

            {session.phase === 'url' && !reportRoute.restoring && (
                <TbcLogUrlPhase
                    logUrlsText={session.logUrlsText}
                    loading={session.loading}
                    onLogUrlsChange={session.setLogUrlsText}
                    onLoadLogs={() => {
                        void session.handleLoadLogs();
                    }}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'selection' && (
                <TbcSelectionPhase
                    loadedLogUrls={session.loadedLogUrls}
                    players={session.players}
                    bosses={session.bosses}
                    selectedPlayerNames={selectedPlayerNames}
                    fightFilter={session.fightFilter}
                    selectedEncounterIds={session.selectedEncounterIds}
                    minDurationSeconds={session.minDurationSeconds}
                    loading={session.loading}
                    analyzeButtonLabel={analyzeButtonLabel}
                    showMinDurationFilter
                    minDurationHelpText="Ignores fights shorter than this."
                    onTogglePlayerName={handleTogglePlayerName}
                    onSelectAllPlayers={() => setSelectedPlayerNames(session.players.map((player) => player.name))}
                    onClearPlayers={() => setSelectedPlayerNames([])}
                    onFightFilterChange={session.setFightFilter}
                    onToggleEncounterId={session.toggleEncounterId}
                    onToggleAllEncounterIds={session.toggleAllEncounterIds}
                    onToggleRaidEncounterIds={session.toggleRaidEncounterIds}
                    onMinDurationChange={session.setMinDurationSeconds}
                    onAnalyze={() => {
                        void handleProcessAnalysis();
                    }}
                    onBack={handleBackToUrl}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'results' && result && (
                <div className="results-phase">
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        title="Stat Values"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={statValueHelp}
                    />

                    {singleHealer ? (
                        <HealerStatValueDetail
                            result={singleHealer}
                            filterLabel={filterLabel}
                            breakdownRef={resultsRef}
                        />
                    ) : (
                        <>
                            <p className="results-status">
                                {healers.length} healers
                                {filterLabel ? ` · ${filterLabel}` : ''}
                                {result.missingPlayerNames.length > 0
                                    ? ` · missing ${result.missingPlayerNames.join(', ')}`
                                    : ''}
                            </p>
                            <p className="analyzer-description">Click a healer for their full breakdown.</p>
                            <div ref={resultsRef}>
                                <TbcSimpleBreakdownTable
                                    rows={sortedHealers}
                                    columns={COMPARISON_COLUMNS.map((column) => ({
                                        key: column.key,
                                        headerClassName: 'sortable-th',
                                        header: (
                                            <span
                                                onClick={() => handleComparisonSort(column.key)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        handleComparisonSort(column.key);
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {column.label}
                                                {comparisonSortKey === column.key
                                                    ? comparisonSortDirection === 'desc'
                                                        ? ' ↓'
                                                        : ' ↑'
                                                    : ''}
                                            </span>
                                        ),
                                        render: (healer: SpellUsageAnalysisData) =>
                                            column.key === 'name' && healerKey(healer) === focusedHealerKey ? (
                                                <strong>▾ {column.render(healer)}</strong>
                                            ) : (
                                                column.render(healer)
                                            ),
                                    }))}
                                    rowKey={healerKey}
                                    emptyMessage="No healers found with the selected filter."
                                    onRowClick={(healer) => {
                                        const key = healerKey(healer);
                                        setFocusedHealerKey((current) => (current === key ? null : key));
                                    }}
                                />
                            </div>

                            {focusedHealer && (
                                <div style={{ marginTop: '24px' }}>
                                    <HealerStatValueDetail
                                        key={healerKey(focusedHealer)}
                                        result={focusedHealer}
                                        filterLabel={filterLabel}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatValueAnalyzer;
